import {
  Theme,
  border,
  color,
  focusStyles,
  propertyColorStyles,
  rem,
  reset,
  tint,
  radius,
} from '@thiememeulenhoff/lit-sdk';
import { PropertyColor } from '@thiememeulenhoff/types';
import { css, unsafeCSS } from 'lit';
import { token } from '@thiememeulenhoff/tokens';
import { tagName } from '../../helpers';

export const tag = tagName('icon-button');
const theme = new Theme(tag);

const propertyColorCallback = (buttonColor: PropertyColor) => {
  const selector = unsafeCSS(`[color=${buttonColor}]`);

  return css`
    :host(${selector}) :not([disabled]) {
      :where(.button[underlined] label) {
        border-block-end-color: ${color(buttonColor, 600)};
      }

      :where(a-icon) {
        color: ${color(buttonColor, 600)};
      }

      :where(.button:hover a-icon) {
        color: ${color(buttonColor, 700)};
      }

      :where(.button:active a-icon) {
        color: ${color(buttonColor, 800)};
      }
    }

    :host(${selector}[variant='ghost']) .button:not([disabled]) {
      background-color: transparent;

      :where(.button[underlined] label) {
        border-block-end-color: ${color('grey', 600)};
      }

      :where(a-icon) {
        color: ${color('grey', 600)};
      }

      &:where(:hover, :active) {
        background-color: ${color('beige', 100)};
      }

      :where(.button[underlined]:hover label) {
        border-block-end-color: ${color('grey', 700)};
      }

      :where(.button:hover a-icon) {
        color: ${color('grey', 700)};
      }
    }

    :host(${selector}[variant='ghost'][active]) .button {
      background-color: ${color('beige', 100)};
    }

    :host(:where([variant='dual'])) :where(.button) {
      background-color: ${color('grey', 300)};
      ${theme.props([
        ['box-shadow', 'box-shadow', token('elevation-shadow-raised')],
        ['border-radius', 'border-radius', rem(12)],
      ])};
    }

    .small {
      font-size: ${rem(12)};
    }

    .default {
      font-size: 1em;
    }

    .medium {
      font-size: ${rem(20)};
    }

    .large {
      font-size: ${rem(24)};
    }

    .larger {
      font-size: ${rem(28)};
    }

    .largest {
      font-size: ${rem(32)};
    }
  `;
};

export const styles = [
  reset.button,
  css`
    :host {
      display: inline-flex;
    }

    :where(.button) {
      align-items: center;
      background-color: ${tint(100)};
      color: ${color('black')};
      display: inline-flex;
      place-content: center;
      position: relative;
      text-decoration: none;

      ${theme.props([
        ['min-block-size', 'min-block-size', rem(40)],
        ['min-inline-size', 'min-inline-size', rem(40)],
        ['gap', 'gap', rem(8)],
        ['padding', 'padding', rem(8, 12)],
        ['border-radius', 'border-radius', rem(20)],
      ])};
    }

    :where(.button),
    :where(.button) * {
      cursor: pointer;
    }

    :where(a-icon) {
      min-inline-size: ${rem(24)};
    }

    :where(.without-content) {
      padding: 0;
    }

    :where(.button:focus-visible) {
      ${focusStyles};
    }

    :where(.button:hover) {
      background-color: ${tint(200)};
    }

    :where(.button[underlined] label) {
      border-block-end: 1px dashed ${color('black')};
      border-block-start: 1px solid transparent;
    }
  `,
  propertyColorStyles(propertyColorCallback),
  css`
    :where(.button[disabled]) {
      background-color: ${tint(400)};
      color: ${tint(500)};
      cursor: default;
      pointer-events: none;

      &[underlined] label {
        border-block-end: ${border(1, 'neutral', 500, 'dashed')};
      }

      label {
        color: ${tint(500)};
      }
    }

    :where(.indicator) {
      background: ${color('pink')};
      block-size: ${rem(10)};
      border-radius: ${radius('extra-large')};
      inline-size: ${rem(10)};
      inset-block-start: ${rem(0)};
      inset-inline-end: ${rem(0)};
      position: absolute;
    }
  `,
];
